import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import './ChatSection.scss';
import { Loader } from '../../../../Common';
import DataMistakesModal from './DataMistakesModal.jsx';
import ChatSearchBar from './ChatSearchBar.jsx';
import ChatSectionFooter from './ChatSectionFooter.jsx';
import ImageViewer from './ImageViewer.jsx';
import VirtualizedMessages from './VirtualizedMessages.jsx';
import { useVirtualizedMessages } from '../../../../../utils/messagesUtils.js';
import { selectedUserIdSelector } from '../../../../../selectors/userSelectors.js';
import { useGetUserMessagesQuery } from '../../../../../api/messagesApi.js';
import { Box, Stack, Typography } from '@mui/material';

const ChatSection = () => {
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [largeMedialUrl, setLargeMedialUrl] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const messagesRef = useRef(null);

  const selectedUserId = useSelector(selectedUserIdSelector);

  const { currentData: messages = [], isFetching: areMessagesFetching } = useGetUserMessagesQuery(selectedUserId);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setSearchQuery('');
    setDebouncedQuery('');
  }, [selectedUserId]);

  const filteredMessages = useMemo(() => {
    if (!debouncedQuery.trim()) {
      return messages;
    }
    const lowerQuery = debouncedQuery.toLowerCase();
    return messages.filter((msg) => {
      if (msg.body?.toLowerCase().includes(lowerQuery)) {
        return true;
      }
      if (msg.attachments?.some((att) => att.filename?.toLowerCase().includes(lowerQuery))) return true;
      return false;
    });
  }, [messages, debouncedQuery]);

  const [messagesItems, messagesVirtualizer] = useVirtualizedMessages({
    getScrollElement: () => messagesRef.current,
    open: true,
    estimateSize: () => 35,
    messages: filteredMessages
  });

  /**
   * @description Open Image Viewer to see large image
   * @param {String} mediaUrl
   */
  const openImageViewer = (mediaUrl) => {
    setShowImageViewer(true);
    setLargeMedialUrl(mediaUrl);
  };

  /**
   * @description Close Image Viewer
   */
  const closeImageViewer = () => {
    setShowImageViewer(false);
    setLargeMedialUrl('');
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        background: 'white',
        borderColor: '#d7dfe3',
        borderRadius: '4px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '400px'
      }}
    >
      <DataMistakesModal />
      {showImageViewer && <ImageViewer imageUrl={largeMedialUrl} onClose={() => closeImageViewer()} />}
      <Box
        sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          border: '1px solid #d7dfe3',
          borderRadius: '4px'
        }}
      >
        {areMessagesFetching && messages.length === 0 ? (
          <Stack
            sx={{
              height: '100%',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'rgba(87, 185, 255, 0.2)'
            }}
          >
            <Loader />
          </Stack>
        ) : (
          <VirtualizedMessages
            onClickImage={openImageViewer}
            ref={messagesRef}
            virtualizer={messagesVirtualizer}
            items={messagesItems}
            searchQuery={debouncedQuery}
          />
        )}
      </Box>
      <ChatSearchBar
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClear={() => {
          setSearchQuery('');
          setDebouncedQuery('');
        }}
        filteredCount={filteredMessages.length}
        totalCount={messages.length}
        isFiltering={debouncedQuery.trim().length > 0}
      />
      {debouncedQuery.trim() && filteredMessages.length === 0 && (
        <Typography variant='body2' sx={{ color: '#999', textAlign: 'center', py: 1 }}>
          No messages match your search
        </Typography>
      )}
      <ChatSectionFooter />
    </Box>
  );
};

export default ChatSection;
